/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { Menu } from '@pushpay/menu';
import { clsx } from '@pushpay/styles';
import { useMediaBreakpoint } from '@pushpay/theming';
import { useOnClickOutside } from '@pushpay/utils';
import { OrgSwitcherSummary } from './OrgSwitcherSummary';
import { OrgSwitcherItem } from './OrgSwitchItem';
import { SearchBar } from './SearchBar';
import useStyles from './use-styles';
export { OrgSwitcher as OrgSwitcherOld } from './OrgSwitcherOld';

const OrgSwitcher = props => {
  const {
    classes: classesProp,
    className: classNameProp,
    'data-pp-at-target': targetId,
    organizations,
    currentOrgKey,
    onItemSelected,
    isLoadingOrg = false,
    buttonLabel,
    placeholder,
    clearButtonAriaLabel: closeButtonAriaLabel
  } = props;
  const classes = useStyles(classesProp);
  const [isPanelOpen, setPanelOpen] = useState(false);
  const ref = useRef(null);
  useOnClickOutside([ref], () => setPanelOpen(false), isPanelOpen);
  const isMobile = useMediaBreakpoint('MOBILE');
  const [searchText, setSearchText] = useState('');
  const currentOrganization = useRef(getCurrentOrg());

  if (!isLoadingOrg) {
    currentOrganization.current = getCurrentOrg();
  }

  const {
    logoUrl: currentOrgLogoUrl,
    name: currentOrgName
  } = currentOrganization.current;
  const [sortedOrganizations, setSortedOrganizations] = useState(sortOrganizations(currentOrganization.current, organizations));
  useEffect(() => {
    if (!isLoadingOrg) {
      setSortedOrganizations(sortOrganizations(currentOrganization.current, organizations));
    }
  }, [currentOrganization, isLoadingOrg, organizations]);
  const overlay = /*#__PURE__*/React.createElement("div", {
    className: classes.orgOverlayWrapper
  }, /*#__PURE__*/React.createElement(Menu, {
    selectedItem: currentOrgKey,
    focusBehavior: "arrowKeys",
    onItemSelected: orgKey => {
      if (onItemSelected) {
        onItemSelected(orgKey);
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: clsx(classes.options)
  }, sortedOrganizations.filter(org => org.name.toLowerCase().includes(searchText)).map(({
    key,
    logoUrl,
    name,
    url,
    to
  }) => {
    const linkProps = {
      href: url,
      to
    };
    return /*#__PURE__*/React.createElement(OrgSwitcherItem, {
      key: key,
      name: name,
      logoUrl: logoUrl,
      linkProps: linkProps,
      orgKey: key,
      isLoading: currentOrgKey === key && isLoadingOrg
    });
  }))));

  const onOrgSummaryClick = () => setPanelOpen(!isPanelOpen);

  const fadeInStyles = useSpring({
    from: {
      opacity: 0
    },
    to: {
      opacity: isPanelOpen ? 1 : 0
    },
    config: {
      duration: 150
    }
  });
  const singleOrganization = organizations.length === 1;
  return /*#__PURE__*/React.createElement("div", {
    className: clsx(classNameProp, classes.root),
    ref: ref,
    "data-pp-at-target": targetId
  }, /*#__PURE__*/React.createElement(OrgSwitcherSummary, {
    "data-pp-at-target": targetId,
    isPanelOpen: isPanelOpen,
    onClick: onOrgSummaryClick,
    currentOrgName: currentOrgName,
    currentOrgLogoUrl: currentOrgLogoUrl,
    isMobile: isMobile,
    singleOrganization: singleOrganization,
    buttonLabel: `${singleOrganization ? currentOrgName : `${currentOrgName} - ${buttonLabel}`}`
  }), isPanelOpen && /*#__PURE__*/React.createElement(animated.div, {
    style: fadeInStyles,
    className: classes.orgSwitcherPanel
  }, /*#__PURE__*/React.createElement(SearchBar, {
    placeholder: placeholder,
    onChange: setSearchText,
    clearButtonAriaLabel: closeButtonAriaLabel
  }), overlay));

  function getCurrentOrg() {
    return organizations.find(x => x.key === currentOrgKey);
  }
};

const sortOrganizations = (currentOrganization, organizations) => [currentOrganization, ...[...organizations].sort((a, b) => a.name.localeCompare(b.name)).filter(i => i.key !== currentOrganization.key)];

export default OrgSwitcher;